import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Card,
  CardContent,
  Grid,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Chip,
  CircularProgress,
  Alert,
  Button,
  Tabs,
  Tab,
} from '@mui/material';
import {
  CheckCircle,
  Cancel,
  Visibility,
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-hot-toast';
import api from '../../services/api';

const MyResults = () => {
  const navigate = useNavigate();
  const [attempts, setAttempts] = useState([]);
  const [filteredAttempts, setFilteredAttempts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [tabValue, setTabValue] = useState(0); // 0: Tous, 1: Réussis, 2: Échoués

  useEffect(() => {
    fetchAttempts();
  }, []);

  useEffect(() => {
    filterAttempts();
  }, [tabValue, attempts]);

  const fetchAttempts = async () => {
    try {
      setLoading(true);
      setError('');
      
      console.log('📊 Fetching my attempts...');
      const response = await api.get('/attempt/my-attempts');
      console.log('✅ Attempts:', response.data);
      
      const attemptsList = response.data.attempts || [];
      setAttempts(attemptsList);
      setFilteredAttempts(attemptsList);
    } catch (err) {
      const errorMsg = err.response?.data?.message || 'Erreur lors du chargement des résultats';
      setError(errorMsg);
      toast.error(errorMsg);
      console.error('❌ My results error:', err);
    } finally {
      setLoading(false);
    }
  };

  const filterAttempts = () => {
    if (tabValue === 0) {
      setFilteredAttempts(attempts);
    } else if (tabValue === 1) {
      setFilteredAttempts(attempts.filter(a => a.passed));
    } else {
      setFilteredAttempts(attempts.filter(a => !a.passed));
    }
  };

  const formatDate = (date) => {
    if (!date) return 'N/A';
    return new Date(date).toLocaleDateString('fr-FR', {
      day: 'numeric',
      month: 'short',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '50vh' }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box>
      <Typography variant="h4" gutterBottom>
        Mes Résultats
      </Typography>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      {/* Stats globales */}
      <Grid container spacing={3} sx={{ mb: 3 }}>
        <Grid item xs={12} md={4}>
          <Card>
            <CardContent>
              <Typography variant="body2" color="text.secondary">
                Total de tentatives
              </Typography>
              <Typography variant="h3" color="primary">
                {attempts.length}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} md={4}>
          <Card>
            <CardContent>
              <Typography variant="body2" color="text.secondary">
                Réussies
              </Typography>
              <Typography variant="h3" color="success.main">
                {attempts.filter(a => a.passed).length}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} md={4}>
          <Card>
            <CardContent>
              <Typography variant="body2" color="text.secondary">
                Taux de réussite
              </Typography>
              <Typography variant="h3" color="warning.main">
                {attempts.length > 0 
                  ? Math.round((attempts.filter(a => a.passed).length / attempts.length) * 100)
                  : 0}%
              </Typography>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Filtres par onglets */}
      <Card sx={{ mb: 2 }}>
        <Tabs value={tabValue} onChange={(e, newValue) => setTabValue(newValue)}>
          <Tab label={`Tous (${attempts.length})`} />
          <Tab label={`Réussis (${attempts.filter(a => a.passed).length})`} />
          <Tab label={`Échoués (${attempts.filter(a => !a.passed).length})`} />
        </Tabs>
      </Card>

      {/* Tableau des résultats */}
      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>Quiz</TableCell>
              <TableCell align="center">Score</TableCell>
              <TableCell align="center">Résultat</TableCell>
              <TableCell align="center">Date</TableCell>
              <TableCell align="right">Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {filteredAttempts.length === 0 ? (
              <TableRow>
                <TableCell colSpan={5} align="center">
                  <Typography color="text.secondary" sx={{ py: 4 }}>
                    Aucun résultat
                  </Typography>
                </TableCell>
              </TableRow>
            ) : (
              filteredAttempts.map((attempt) => (
                <TableRow key={attempt._id} hover>
                  <TableCell>
                    <Typography variant="body1">
                      {attempt.quiz?.title || 'Quiz'}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      Tentative #{attempt.attemptNumber}
                    </Typography>
                  </TableCell>
                  <TableCell align="center">
                    <Typography 
                      variant="h6" 
                      color={attempt.passed ? 'success.main' : 'error.main'}
                    >
                      {attempt.score}%
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      {attempt.pointsEarned}/{attempt.maxPoints} points
                    </Typography>
                  </TableCell>
                  <TableCell align="center">
                    <Chip
                      label={attempt.passed ? 'Réussi' : 'Échoué'}
                      color={attempt.passed ? 'success' : 'error'}
                      size="small"
                      icon={attempt.passed ? <CheckCircle /> : <Cancel />}
                    />
                  </TableCell>
                  <TableCell align="center">
                    <Typography variant="body2">
                      {formatDate(attempt.completedAt || attempt.createdAt)}
                    </Typography>
                  </TableCell>
                  <TableCell align="right">
                    <Button
                      size="small"
                      variant="outlined"
                      startIcon={<Visibility />}
                      onClick={() => navigate(`/results/${attempt._id}`)}
                    >
                      Détails
                    </Button>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </TableContainer>
    </Box>
  );
};

export default MyResults;

